import type { Category } from '../scoring/types';

/**
 * Why a lift is notable.
 *
 * Evaluated **as of the moment the lift was logged** - it beat everything that
 * came before it - rather than against today's records. A March personal best
 * stays marked forever, and the feed never silently rewrites itself when a newer
 * lift lands.
 */
export interface FeedBadges {
  /** Beat every earlier lift in this category: a category personal best. */
  isCategoryPr: boolean;
  /**
   * Took the lead for its category within its own calendar month. Suppressed
   * when isCategoryPr is set, which is strictly the stronger claim.
   */
  isCategoryMonthBest: boolean;
  /** Beat every earlier attempt at this specific exercise. */
  isExercisePr: boolean;
}

/** The lift being judged. */
export interface BadgeCandidate {
  exerciseId: string;
  points: number;
  estimated1RmLb: number;
}

/** What the same lifter had already achieved, before this lift. */
export interface PriorBests {
  /** Best points in this category, all time. */
  categoryPoints?: number;
  /** Best points in this category within this lift's own month. */
  categoryMonthPoints?: number;
  /** Best estimated 1RM for this exact exercise, all time. */
  exercise1Rm?: number;
}

/**
 * Decides which badges a lift earns.
 *
 * Shared by the feed, which replays history, and the log form, which judges a
 * lift the instant it is submitted. One rule in one place: the award you see on
 * the confirmation screen is by construction the one the feed will show.
 *
 * Which number counts as "better" differs by badge on purpose:
 *
 * - Category badges compare **points**. A category is a scoring construct that
 *   spans different exercises, and points is the currency it is denominated in -
 *   the same thing the leaderboard ranks on.
 * - The exercise badge compares **estimated 1RM**. One exercise is a physical
 *   thing, so "a personal best in that lift" means you moved more weight,
 *   independent of what body weight did to your score in between.
 */
export function awardBadges(lift: BadgeCandidate, prior: PriorBests): FeedBadges {
  const isCategoryPr = prior.categoryPoints === undefined || lift.points > prior.categoryPoints;
  const isCategoryMonthBest =
    !isCategoryPr &&
    (prior.categoryMonthPoints === undefined || lift.points > prior.categoryMonthPoints);
  const isExercisePr =
    !!lift.exerciseId &&
    (prior.exercise1Rm === undefined || lift.estimated1RmLb > prior.exercise1Rm);

  return { isCategoryPr, isCategoryMonthBest, isExercisePr };
}

/** True when a lift earned any award at all. */
export function hasAnyBadge(badges: FeedBadges): boolean {
  return badges.isCategoryPr || badges.isCategoryMonthBest || badges.isExercisePr;
}

/**
 * Where a lift moved its lifter on that month's leaderboard.
 *
 * Only recorded when the rank actually improved. Someone logging their first
 * lift of a month is treated as sitting just below everyone already on the
 * board, so arriving in last place is not reported as a climb - but overtaking
 * somebody on the way in is.
 */
export interface RankMovement {
  from: number;
  to: number;
}

/** Monthly total: the sum of the lifter's best points in each category. */
export function totalOfBests(bests: Partial<Record<Category, number>>): number {
  return Object.values(bests).reduce((sum: number, points) => sum + (points ?? 0), 0);
}
